import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct, fileUrl } from "../lib/api";
import { InquiryForm } from "../components/InquiryForm";
import { ArrowLeft, PlayCircle, Edit } from "lucide-react";
import { useIsAdmin } from "../lib/useIsAdmin";
import { useSeo } from "../lib/useSeo";
import { WhatsAppButton } from "../components/WhatsAppButton";

function getEmbedUrl(url) {
  if (!url) return null;
  // youtube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return url;
}

function isDirectVideo(url) {
  return url && /\.(mp4|webm|ogg)(\?|$)/i.test(url);
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const isAdmin = useIsAdmin();
  useSeo({
    title: product ? product.name : "Product",
    description: product ? `${product.name} — ${(product.description || "").slice(0, 150)}` : undefined,
  });

  useEffect(() => {
    getProduct(id).then(setProduct).catch(() => setError("Product not found"));
  }, [id]);

  if (error) return <div className="max-w-7xl mx-auto p-16 text-center">{error}</div>;
  if (!product) return <div className="max-w-7xl mx-auto p-16 text-center text-muted-foreground">Loading...</div>;

  const embed = getEmbedUrl(product.video_url);
  const direct = isDirectVideo(product.video_url) || (product.video_url && product.video_url.startsWith("/api/"));
  const resolvedVideoSrc = direct ? fileUrl(product.video_url) : embed;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="flex items-center justify-between mb-8">
        <Link to="/products" data-testid="back-to-products" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft className="w-4 h-4" /> Back to products
        </Link>
        {isAdmin && (
          <Link
            to={`/admin?edit=${product.id}`}
            data-testid="detail-edit-btn"
            className="inline-flex items-center gap-2 bg-secondary text-white hover:bg-slate-800 transition-colors font-medium rounded-full px-5 py-2 text-sm"
          >
            <Edit className="w-4 h-4" strokeWidth={2} /> Edit product
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-5 gap-6 lg:gap-10">
        <div className="lg:col-span-3">
          <div className="aspect-[4/3] bg-white border border-border rounded-2xl overflow-hidden mb-6">
            <img
              src={fileUrl(product.image_url)}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-image"
            />
          </div>

          {product.video_url ? (
            <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="product-video-section">
              <div className="p-4 border-b border-border flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-primary" strokeWidth={1.75} />
                <div className="font-heading font-semibold">Product Video</div>
              </div>
              <div className="aspect-video bg-black">
                {direct ? (
                  <video src={resolvedVideoSrc} controls className="w-full h-full" data-testid="product-video-native" />
                ) : (
                  <iframe
                    src={embed}
                    title={product.name}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="product-video-iframe"
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3" data-testid="product-category">
            {product.category}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3 sm:mb-4" data-testid="product-name">
            {product.name}
          </h1>
          <div className="text-2xl sm:text-3xl font-bold text-secondary mb-4 sm:mb-6" data-testid="product-price">
            ₹{Number(product.price).toLocaleString("en-IN")}
          </div>
          <p className="text-muted-foreground leading-relaxed mb-6" data-testid="product-description">
            {product.description}
          </p>
          <div className="mb-8">
            <WhatsAppButton productName={product.name} testid="wa-detail-btn" />
          </div>

          <div className="bg-white border border-border rounded-2xl p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-2">Interested?</div>
            <h3 className="font-heading text-2xl font-bold mb-4">Request a quote</h3>
            <InquiryForm prefillProduct={product.name} />
          </div>
        </div>
      </div>
    </div>
  );
}
